import React, { useState, useEffect } from 'react';
import { Calendar, Users, Video, Clock, CheckCircle, FileText, Star, Link, Plus, ChevronRight, MessageSquare, Briefcase, MapPin, Mail, Loader2 } from 'lucide-react';
import { supabase } from '../services/supabaseService';
import { Interview } from '../types';
import ScorecardConfigModal from './ScorecardConfigModal';

interface InterviewFlowProps {
  vacancyId: string;
}

export default function InterviewFlow({ vacancyId }: InterviewFlowProps) {
  const [activeTab, setActiveTab] = useState<'upcoming' | 'scorecards' | 'templates'>('upcoming');
  const [interviews, setInterviews] = useState<any[]>([]);
  const [feedbacks, setFeedbacks] = useState<any[]>([]);
  const [templatesList, setTemplatesList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showScorecardConfig, setShowScorecardConfig] = useState(false);

  useEffect(() => {
    const fetchInterviewsAndFeedbacks = async () => {
      setLoading(true);
      try {
        const { data: ivs } = await supabase
          .from('interviews')
          .select('*, candidates(name, role, photo_url)')
          .eq('vacancy_id', vacancyId)
          .gte('scheduled_at', new Date().toISOString())
          .order('scheduled_at', { ascending: true });
        
        if (ivs) setInterviews(ivs);

        const { data: fbData } = await supabase
          .from('candidate_feedback')
          .select('*, interviews!inner(vacancy_id, candidate_id, candidates(name, photo_url))')
          .eq('interviews.vacancy_id', vacancyId)
          .order('created_at', { ascending: false })
          .limit(10);
        
        if (fbData) setFeedbacks(fbData);

        const { data: tData } = await supabase
          .from('automation_templates')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(4);
        
        if (tData) setTemplatesList(tData);

      } catch (e) {
        console.error('Failed to fetch upcoming interviews or feedbacks', e);
      } finally {
        setLoading(false);
      }
    };
    fetchInterviewsAndFeedbacks();
  }, [vacancyId]);

  return (
    <div className="flex flex-col h-full bg-slate-50/50 rounded-[2.5rem] relative overflow-hidden animate-fadeIn">
      {/* Header */}
      <div className="px-8 py-6 border-b border-slate-200 bg-white flex justify-between items-center rounded-t-[2.5rem]">
        <div>
          <h2 className="text-2xl font-black text-slate-900 tracking-tight flex items-center gap-2">
            Interview Flow <span className="text-[10px] font-bold bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full uppercase tracking-widest mt-1">Bèta</span>
          </h2>
          <p className="text-slate-500 text-sm font-medium mt-1">
            Organiseer, plan en evalueer je gesprekken gestructureerd en data-gedreven.
          </p>
        </div>
        <button className="bg-slate-900 text-white px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 text-sm hover:bg-black transition-all shadow-sm hover:shadow">
          <Calendar size={16} /> Plan In
        </button>
      </div>

      {/* Tabs */}
      <div className="px-8 py-4 border-b border-slate-200 bg-white flex gap-6">
        {[
          { id: 'upcoming', label: 'Komende Gesprekken', icon: <Clock size={16} /> },
          { id: 'scorecards', label: 'Scorecards', icon: <Star size={16} /> },
          { id: 'templates', label: 'Voorbereiding & Templates', icon: <Briefcase size={16} /> }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 pb-3 px-1 border-b-2 font-bold transition-all text-sm ${
              activeTab === tab.id
                ? 'border-[#e51b23] text-[#e51b23]'
                : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Content Area */}
      <div className="flex-1 p-8 overflow-y-auto">
        {activeTab === 'upcoming' && (
          <div className="space-y-6">
            {loading ? (
              <div className="flex justify-center p-8"><Loader2 className="animate-spin text-slate-300" size={32} /></div>
            ) : interviews.length > 0 ? (
              interviews.map(iv => (
                <div key={iv.id} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm relative overflow-hidden group hover:border-red-200 transition-colors cursor-pointer">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-50 to-rose-50/50 rounded-bl-full -z-10 transition-transform group-hover:scale-110"></div>
                  
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400 font-black text-lg shadow-[inset_0_2px_4px_rgba(0,0,0,0.02)] border border-slate-100 overflow-hidden">
                        {iv.candidates?.photo_url ? (
                          <img src={iv.candidates.photo_url} alt={iv.candidates.name} className="w-full h-full object-cover" />
                        ) : (
                          iv.candidates?.name?.charAt(0) || '?'
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-black text-slate-900 leading-tight">{iv.candidates?.name || 'Onbekende Kandidaat'}</h3>
                        <p className="text-slate-500 text-sm font-medium">{iv.candidates?.role || 'Kandidaat'} • {iv.duration_minutes} min</p>
                      </div>
                    </div>
                    <span className="bg-amber-50 text-amber-600 px-3 py-1 rounded-lg text-xs font-bold border border-amber-100 flex items-center gap-1.5">
                      <Clock size={12} /> {new Date(iv.scheduled_at).toLocaleString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    <div className="flex items-center gap-2 text-slate-600 text-xs font-bold">
                      {iv.location?.toLowerCase().includes('teams') || iv.location?.toLowerCase().includes('meet') || iv.location?.toLowerCase().includes('zoom') ? <Video size={14} className="text-slate-400" /> : <MapPin size={14} className="text-slate-400" />}
                      {iv.location || 'Online'}
                    </div>
                    {iv.interviewers && iv.interviewers.length > 0 && (
                      <div className="flex items-center gap-2 text-slate-600 text-xs font-bold">
                        <Users size={14} className="text-slate-400" /> {iv.interviewers[0]}
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-slate-600 text-xs font-bold justify-self-end col-span-2">
                      <button className="text-red-600 hover:underline flex items-center gap-1">Open Scorecard <ChevronRight size={14} /></button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center bg-slate-50/50 hover:bg-slate-50 transition-colors">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-400">
                  <Calendar size={20} />
                </div>
                <h4 className="font-bold text-slate-700 mb-1">Geen komende gesprekken gepland</h4>
                <p className="text-slate-500 text-sm">Ga naar de Shortlist & Benaderen tab om kandidaten in te plannen.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'scorecards' && (
          <div className="space-y-6 animate-fadeIn">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50/50 border border-green-100 rounded-2xl p-8 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-black text-slate-900 mb-2">Uniform Beoordelen</h3>
                <p className="text-slate-600 text-sm max-w-lg">Implementeer een scorecard gebaseerd op de belangrijkste competenties. Verklein bias en maak vergelijken makkelijker.</p>
              </div>
              <button 
                onClick={() => setShowScorecardConfig(true)}
                className="bg-white/80 backdrop-blur text-green-700 px-5 py-2.5 rounded-xl font-bold border border-green-200 shadow-sm hover:bg-white transition-all text-sm"
              >
                Scorecard Configureren
              </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
               <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                 <h4 className="font-bold text-slate-800 text-sm">Recente Beoordelingen</h4>
               </div>
               {feedbacks.length > 0 ? (
                 <div className="divide-y divide-slate-100">
                   {feedbacks.map(fb => (
                     <div key={fb.id} className="p-5 hover:bg-slate-50 transition-colors flex justify-between items-center group">
                       <div className="flex items-center gap-4">
                         <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center font-bold text-slate-400">
                           {fb.interviews?.candidates?.photo_url ? (
                             <img src={fb.interviews.candidates.photo_url} alt="Kandidaat" className="w-full h-full object-cover" />
                           ) : (
                             fb.interviews?.candidates?.name?.charAt(0) || '?'
                           )}
                         </div>
                         <div>
                           <h4 className="font-bold text-slate-800 text-sm">{fb.interviews?.candidates?.name || 'Onbekende Kandidaat'}</h4>
                           <p className="text-xs text-slate-500 font-medium">{new Date(fb.created_at).toLocaleDateString('nl-NL')} • Door {fb.reviewer_name || 'Recruiter'}</p>
                         </div>
                       </div>
                       <div className="flex items-center gap-6">
                         <div className="text-right">
                           <div className="flex items-center justify-end gap-1 mb-1">
                             {[...Array(5)].map((_, i) => (
                               <Star key={i} size={12} className={i < (fb.overall_score || 0) ? "text-amber-400 fill-amber-400" : "text-slate-200"} />
                             ))}
                           </div>
                           <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${
                             fb.recommendation === 'Strong Hire' ? 'bg-emerald-100 text-emerald-700' :
                             fb.recommendation === 'Hire' ? 'bg-green-100 text-green-700' :
                             fb.recommendation === 'Maybe' ? 'bg-amber-100 text-amber-700' :
                             'bg-red-100 text-red-700'
                           }`}>{fb.recommendation}</span>
                         </div>
                         <button className="text-slate-400 hover:text-red- transition-colors">
                           <ChevronRight size={18} />
                         </button>
                       </div>
                     </div>
                   ))}
                 </div>
               ) : (
                 <div className="p-6 text-center text-slate-500 text-sm">
                   Nog geen scorecards ingevuld voor actieve kandidaten.
                 </div>
               )}
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="space-y-6 animate-fadeIn">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm group hover:-translate-y-1 transition-all">
                <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center mb-4">
                  <FileText size={20} />
                </div>
                <h3 className="font-black text-slate-900 mb-2">Voorbereidingen Genereren</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">Laat Froukje AI automatisch een voorbereiding maken op basis van het CV van de kandidaat, de vacature en markttrends.</p>
                <button onClick={() => alert('AI Voorbereiding is beschikbaar in de kandidaat-detail modal tijdens het beoordelen.')} className="text-indigo-600 font-bold text-sm flex items-center gap-1 group-hover:underline">
                  Bekijk AI Voorbereiding <ChevronRight size={16} />
                </button>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm group hover:-translate-y-1 transition-all">
                <div className="w-10 h-10 bg-orange-50 text-orange-600 rounded-xl flex items-center justify-center mb-4">
                  <Mail size={20} />
                </div>
                <h3 className="font-black text-slate-900 mb-2">Uitnodigingen & Templates</h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-4">Beheer de standaard templates voor uitnodigingen, afwijzingen, en self-booking links.</p>
                <button onClick={() => alert('Ga naar Automatisering in het hoofdmenu om direct templates aan te maken en beheren.')} className="text-orange-600 font-bold text-sm flex items-center gap-1 group-hover:underline">
                  Systeeminstellingen Openen <ChevronRight size={16} />
                </button>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm mt-6">
               <div className="p-5 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                 <h4 className="font-bold text-slate-800 text-sm">Actieve E-mail Templates</h4>
                 <span className="bg-slate-200 text-slate-600 text-[10px] uppercase font-black px-2 py-0.5 rounded tracking-widest">Global</span>
               </div>
               {templatesList.length > 0 ? (
                 <div className="divide-y divide-slate-100">
                   {templatesList.map(t => (
                     <div key={t.id} className="p-4 hover:bg-slate-50 transition-colors flex justify-between items-center group">
                       <div className="flex items-center gap-3">
                         <div className="w-8 h-8 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center">
                           <Mail size={14} />
                         </div>
                         <div>
                           <h4 className="font-bold text-slate-800 text-sm">{t.name}</h4>
                           <p className="text-[10px] text-slate-500">{t.subject || 'Geen onderwerp'}</p>
                         </div>
                       </div>
                       <div className="text-right">
                         <span className="text-[10px] font-bold text-slate-400 border border-slate-200 px-2 py-1 rounded bg-white">
                           {t.type}
                         </span>
                       </div>
                     </div>
                   ))}
                 </div>
               ) : (
                 <div className="p-6 text-center text-slate-500 text-sm">
                   Geen mail templates gevonden.
                 </div>
               )}
            </div>
          </div>
        )}
      </div>
      
      {showScorecardConfig && (
        <ScorecardConfigModal vacancyId={vacancyId} onClose={() => setShowScorecardConfig(false)} />
      )}
    </div>
  );
}
